import {getStore} from 'src/v2/state/store';
import {usePrepareSessionForSetup} from 'src/v2/components/setup/usePrepareSessionForSetup';
import {useConfirmSessionForSetup} from 'src/v2/components/setup/useConfirmSessionForSetup';
import type {SupportedCurrency} from 'src/v2/state/types';
import {convertWalletTypeToApiType} from 'src/utils/convertWalletTypeToApiType';
import {useSignPermitMessage} from 'src/v2/components/pay/useSignPermitMessage';
import {syncWalletToSession} from 'src/v2/components/pay/syncWalletToSession';
import {useUpdateSessionOnWalletConnect} from 'src/v2/components/walletConnect/useUpdateSessionOnWalletConnect';
import {
  handleError,
  clearErrorBanner,
} from 'src/v2/components/error/handleError';
import {useIntl} from '@sail/intl';
import {useLogger} from 'src/utils/logger';
import {useResyncSessionState} from 'src/v2/components/pay/useResyncSessionState';

export const useHandleSetup = () => {
  const intl = useIntl();
  const store = getStore();
  const prepareSessionForSetup = usePrepareSessionForSetup();
  const confirmSessionForSetup = useConfirmSessionForSetup();
  const {signPermitMessage} = useSignPermitMessage();
  const [updateSession] = useUpdateSessionOnWalletConnect();
  const activeWallet = store.useSelector(
    (state) => state.walletConnection.activeWallet,
  );
  const walletSessions = store.useSelector((state) => state.walletSessions);
  const logger = useLogger();
  const resyncSessionState = useResyncSessionState();

  const handleSetup = async ({
    selectedCurrency,
    setIsProcessing,
    onConfirmSuccess,
  }: {
    selectedCurrency: SupportedCurrency;
    setIsProcessing: (processing: boolean) => void;
    onConfirmSuccess: () => void;
  }): Promise<void> => {
    if (!activeWallet) {
      return;
    }

    clearErrorBanner();

    logger.log('crypto_payin_session.setup_button_pressed', {
      currency: selectedCurrency.id,
      address: activeWallet.address,
      provider: activeWallet.type,
      wallet_key: activeWallet.key,
    });

    try {
      setIsProcessing(true);

      await syncWalletToSession(activeWallet, walletSessions, updateSession);
      const walletProvider = convertWalletTypeToApiType(activeWallet.type);

      await prepareSessionForSetup({
        selectedCurrency: selectedCurrency.assetCode,
        walletAddress: activeWallet.address,
        network: selectedCurrency.currencyNetwork,
        walletProvider,
      });

      store.setValue((state) => {
        state.overlay = 'CONFIRM_SETUP';
      });
      const signature = await signPermitMessage({asset: selectedCurrency});

      store.setValue((state) => {
        state.overlay = null;
      });

      await confirmSessionForSetup({
        signedTransactionData: signature,
        selectedCurrency: selectedCurrency.assetCode,
        walletAddress: activeWallet.address,
        network: selectedCurrency.currencyNetwork,
        walletProvider,
        walletKey: activeWallet.key,
      });
      onConfirmSuccess();
    } catch (error) {
      await resyncSessionState();
      handleError({error, intl, context: 'setup'});
      store.setValue((state) => {
        state.overlay = null;
      });
      console.error('Setup failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return handleSetup;
};
